/**
 * Hooks da visão **tenant-wide** dos contatos autorizados (oversight do DPO no
 * Centro de Direitos). Distinto de `use-bond-authorized-contacts` (bond-scoped,
 * TabCliente): aqui o DPO fiscaliza TODAS as autorizações do tenant num só
 * lugar via `GET /v1/lgpd/authorized-contacts` (gated `lgpd-authorized-contacts-read`).
 *
 * Gatear o fetch (não só o render): a query só dispara quando `enabled` (a cap
 * presente) — senão o GET de recurso negado vazaria 403 em toast.
 *
 * Revogação reusa o DELETE bond-scoped existente (a linha carrega bondId +
 * version) e invalida a chave tenant-wide para refrescar a lista.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  api,
  type RevokeAuthorizedContactRequest,
  type TenantAuthorizedContactWireItem,
} from '../lib/api-client';

const KEY = 'lgpd-authorized-contacts';
const STALE_TIME = 30_000;

export function useTenantAuthorizedContacts(
  includeRevoked: boolean,
  enabled: boolean,
): {
  data: TenantAuthorizedContactWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [KEY, { includeRevoked }],
    queryFn: async () => {
      const res = await api.listTenantAuthorizedContacts({ includeRevoked });
      return res.data;
    },
    enabled,
    staleTime: STALE_TIME,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

interface RevokeVars {
  bondId: string;
  contactId: string;
  version: number;
  body: RevokeAuthorizedContactRequest;
}

export function useRevokeTenantAuthorizedContact(): ReturnType<
  typeof useMutation<TenantAuthorizedContactWireItem, Error, RevokeVars>
> {
  const qc = useQueryClient();
  return useMutation<TenantAuthorizedContactWireItem, Error, RevokeVars>({
    mutationFn: ({ bondId, contactId, version, body }) =>
      api.revokeBondAuthorizedContact(
        bondId,
        contactId,
        version,
        body,
      ) as Promise<TenantAuthorizedContactWireItem>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      // a linha vive também no bond-scoped (TabCliente) — refresca lá também.
      void qc.invalidateQueries({ queryKey: ['bond-authorized-contacts'] });
    },
  });
}
